import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsMounted } from 'src/hooks/useIsMounted';
import { useWorkDocsAuth } from 'src/hooks/useWorkDocsAuth';
import { updateBaseballCardWorkDocsFolderId, fetchEmployeesBatchForOrgChart, sleep } from 'src/api/baseball-card-service';
import { fetchAllReportsRecursively, countAllReportsUnderLeader, fetchDirectReports } from 'src/api/papi-service';
import { findOrCreateFoldersUnder, getTokenExpiry, listFolderContents } from 'src/services/WorkDocsService';
import { logger, handleError } from 'src/logger';
import { OrgChartEmployee } from '../types/orgChart.types';
import {
  buildRootFolderName,
  buildSupplementalPlan,
  buildFlatPlan,
  FIXED_SUBFOLDERS,
  SUPPLEMENTAL_DATA_FOLDER,
  SupplementalPlan
} from '../utils/workDocsFolderPlan';
import { buildWorkDocsFolderUrl } from '../utils/workDocsUrl';
import { runWithConcurrency } from '../utils/concurrency';
import { MAX_EMPLOYEES, MAX_EMPLOYEES_LIMIT_MESSAGE, BATCH_SIZE, BATCH_DELAY_MS } from '../constants/BaseballCardConstants';

// Refresh the WorkDocs token this many ms BEFORE its stated expiry. A provisioning run makes many
// sequential calls; refreshing slightly early avoids handing a folder-create request a token that
// would lapse in the round-trip and fail at the boundary.
const TOKEN_EXPIRY_SKEW_MS = 10_000;

interface UseWorkDocsFolderProvisioningArgs {
  /** The leader whose folder tree is being provisioned (the current org-chart root). */
  leaderAlias: string;
  /**
   * Which org depth to provision under `Supplemental Context`:
   * - `'direct'`: a flat folder per direct report (no nesting).
   * - `'all'`: the full nested manager -> report tree for the whole org.
   * Mirrors the page's Direct/All toggle. Defaults to `'all'` (the original behavior).
   */
  viewMode?: 'direct' | 'all';
}

interface UseWorkDocsFolderProvisioningReturn {
  /** This quarter's provisioned root folder id (`{alias}-baseball-cards-q{n}-{year}`), or null if not yet provisioned. */
  rootFolderId: string | null;
  /** Web URL for the root folder (the "Upload Data Here" link), or null. */
  rootFolderUrl: string | null;
  isProvisioning: boolean;
  isVerifying: boolean;
  progress: { current: number; total: number };
  error: string | null;
  /**
   * Non-blocking notice surfaced when provisioning still succeeded but was scaled back — currently
   * only when an over-cap ({@link MAX_EMPLOYEES}) org fell back to a direct-reports-only folder tree.
   * Unlike `error`, this does NOT close the wizard's forward gate; folders exist and the user may
   * proceed. Null when there is nothing to note.
   */
  warning: string | null;
  /** True when a provision run can start (token present, parent root known, not busy). */
  canProvision: boolean;
  /**
   * Monotonic counter bumped ONLY when an active `provision()` click completes cleanly (folders
   * created AND the reference persisted). The passive load-time verify effect and the
   * partial-failure / token-expiry paths never touch it. Consumers watch it as a one-shot event
   * signal (e.g. to auto-advance the wizard) — each increment is a distinct clean success, so a
   * later re-provision re-fires the effect.
   */
  provisionSuccessCount: number;
  /**
   * True only when the last provision run aborted specifically because the WorkDocs token expired
   * or rotated mid-run. Distinct from a generic `error` so the UI can attach a "Reconnect WorkDocs"
   * action to this case alone. Cleared when a new provision run starts or the leader changes.
   */
  tokenExpired: boolean;
  provision: () => void;
}

/**
 * Orchestrates provisioning of a leader's quarter-scoped WorkDocs folder tree and the inline
 * "Upload Data Here" link.
 *
 * Responsibilities (all state/side-effects live here, none in the .tsx):
 * - On load, verifies whether THIS QUARTER's root exists by listing the user's WorkDocs home
 *   once and matching a child named `{alias}-baseball-cards-q{n}-{year}` — a prior quarter's
 *   folder does NOT count. Found -> surface the link with that id; absent -> the "Set Up
 *   WorkDocs Folders" button. No name is trusted from storage (the name is quarter-specific).
 * - `provision()` fetches the org under the leader, builds a BFS plan, and idempotently
 *   find-or-creates the quarter root -> 5 fixed folders -> the Supplemental Context org tree,
 *   then persists the root id back to the card. In 'all' mode it normally provisions the FULL
 *   org, but if that exceeds {@link MAX_EMPLOYEES} it falls back to a flat direct-reports-only
 *   tree and sets `warning` (folders still exist, so this is not an `error`). Re-runs within the
 *   same quarter (resume after a partial failure, or recover after a deletion) take the exact
 *   same path; a new quarter creates a fresh sibling root.
 *
 * The auth `rootFolderId` is the user's WorkDocs home (the PARENT we create under);
 * the returned `rootFolderId` is this quarter's `{alias}-baseball-cards-q{n}-{year}` folder.
 */
export const useWorkDocsFolderProvisioning = ({
  leaderAlias,
  viewMode = 'all'
}: UseWorkDocsFolderProvisioningArgs): UseWorkDocsFolderProvisioningReturn => {
  const { accessToken, isAuthenticated, rootFolderId: parentRootFolderId, refresh } = useWorkDocsAuth();

  const [rootFolderId, setRootFolderId] = useState<string | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  // Non-blocking notice (e.g. "fell back to direct reports because the org exceeds the cap"). Kept
  // separate from `error` so it never closes the wizard's forward gate.
  const [warning, setWarning] = useState<string | null>(null);
  // Bumped only on a clean active-provision success (see the return type doc). Starts at 0 so a
  // mount never looks like a success to consumers.
  const [provisionSuccessCount, setProvisionSuccessCount] = useState(0);
  // True only when the most recent provision run aborted because the WorkDocs token lapsed/rotated
  // mid-run (the `WORKDOCS_TOKEN_EXPIRED` path). Lets the UI attach a "Reconnect WorkDocs" action to
  // this specific error — a generic partial failure keeps the plain status line. Cleared on the next
  // provision start and on a leader change.
  const [tokenExpired, setTokenExpired] = useState(false);

  const isMounted = useIsMounted();
  // Serializes the passive self-heal check and the active provision run; a provision
  // click sets `supersededByProvision` so an in-flight verify discards its result.
  const opInFlight = useRef(false);
  const supersededByProvision = useRef(false);
  // Live view of the current token for the async provision run. The `useCallback`
  // closure freezes `accessToken` at creation, so comparing it to a snapshot taken
  // inside the same closure can never detect a mid-run rotation — this ref can.
  const accessTokenRef = useRef(accessToken);
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  // Reset per-leader state only when the org-chart root (leader) changes. The root folder
  // is `{alias}-baseball-cards-q{n}-{year}` — its id is independent of Direct/All, and no
  // "provisioned mode" is persisted — so toggling viewMode must NOT null a verified rootFolderId (doing
  // so hid the folder and dropped the UI back to the Set Up button). viewMode still drives
  // what `provision()` builds under Supplemental Context; re-provisioning in another mode is
  // idempotent (find-or-create), so switching modes and clicking Set Up again just extends
  // the tree without losing the root reference.
  useEffect(() => {
    setRootFolderId(null);
    setError(null);
    setWarning(null);
    setTokenExpired(false);
    setProgress({ current: 0, total: 0 });
    supersededByProvision.current = false;
  }, [leaderAlias]);

  // Surface the "Upload Data Here" link only once THIS QUARTER's root folder is confirmed to
  // exist. Because the root name is quarter-scoped (`{alias}-baseball-cards-q{n}-{year}`), we
  // cannot trust a single stored folder id — last quarter's folder still resolves and would
  // masquerade as this quarter's. Instead we list the user's WorkDocs home (the parent we
  // provision under) ONCE and look for a child whose name === this quarter's root name. Found ->
  // adopt its id; absent (only prior quarters exist, or nothing yet) -> fall back to the "Set Up
  // WorkDocs Folders" button. No token or no known parent -> also hidden until both are known.
  useEffect(() => {
    if (!leaderAlias) return;

    // Staleness is tracked with this per-run closure flag (NOT a shared ref): React
    // StrictMode mounts effects twice (mount -> cleanup -> mount), and a shared
    // in-flight lock would be left set by the first run's cleanup and starve the
    // second run. Each run owns its own `cancelled`; only the latest run writes state.
    let cancelled = false;
    setIsVerifying(true);

    const finish = (next: string | null): void => {
      if (cancelled || !isMounted.current || supersededByProvision.current) return;
      setRootFolderId(next);
    };

    const verifyByName = async (): Promise<void> => {
      // Need both a token and the parent home folder to list; until then, keep it hidden.
      // This effect re-runs when either arrives (both are in the dependency array).
      if (!accessToken || !parentRootFolderId) {
        finish(null);
        return;
      }

      const expectedName = buildRootFolderName(leaderAlias);
      try {
        const { folders } = await listFolderContents(parentRootFolderId, accessToken);
        const match = folders.find((folder) => folder.name === expectedName);
        finish(match ? match.id : null);
      } catch (err) {
        // Could not list the parent (token issue, transient failure) -> show the Set Up button.
        logger.warn('Baseball Cards: could not verify this-quarter WorkDocs root folder', { leaderAlias, error: err });
        finish(null);
      }
    };

    void verifyByName().finally(() => {
      if (!cancelled && isMounted.current) setIsVerifying(false);
    });

    return () => {
      cancelled = true;
    };
  }, [leaderAlias, accessToken, parentRootFolderId]);

  const provision = useCallback(async () => {
    if (!leaderAlias || !accessToken || !parentRootFolderId) {
      setError('Connect WorkDocs before setting up folders.');
      return;
    }
    // Block only a concurrent provision run (double-click); a passive verify no longer
    // holds this lock. Take priority over any in-flight verify so its result is dropped.
    if (opInFlight.current) return;
    supersededByProvision.current = true;
    opInFlight.current = true;
    setIsProvisioning(true);
    setError(null);
    setWarning(null);
    setTokenExpired(false);
    setProgress({ current: 0, total: 0 });

    try {
      // Build the Supplemental Context plan for the selected mode:
      // - 'direct': one flat folder per direct report (small by nature, no size guard).
      // - 'all': the full nested org tree, UNLESS the org exceeds the cap — then fall back to a
      //   flat direct-reports-only tree (baseball cards only support MAX_EMPLOYEES people) rather
      //   than provisioning hundreds of folders or failing outright.
      let plan: SupplementalPlan;
      // Tracks the over-cap fallback so we can warn (not error) and skip the auto-advance signal:
      // the user should see the warning on Step 1 before manually continuing.
      let fellBackToDirect = false;
      if (viewMode === 'direct') {
        const directReportAliases = (await fetchDirectReports(leaderAlias)).filter((alias) => alias !== leaderAlias);
        plan = buildFlatPlan(directReportAliases);
      } else {
        const totalReports = await countAllReportsUnderLeader(leaderAlias);
        if (totalReports > MAX_EMPLOYEES) {
          // Over the cap: create folders for the leader's direct reports only and surface a
          // non-blocking warning. This is NOT an error — the folders exist, so the wizard's
          // forward gate stays open. Direct-only is intentionally unguarded for size (a single
          // manager with >MAX_EMPLOYEES direct reports is not realistic — same rationale as the
          // 'direct' branch above).
          fellBackToDirect = true;
          if (isMounted.current) setWarning(MAX_EMPLOYEES_LIMIT_MESSAGE);
          const directReportAliases = (await fetchDirectReports(leaderAlias)).filter((alias) => alias !== leaderAlias);
          plan = buildFlatPlan(directReportAliases);
        } else {
          const reportAliases = (await fetchAllReportsRecursively(leaderAlias)).filter((alias) => alias !== leaderAlias);
          const employeeMap = await fetchEmployeesBatchForOrgChart([leaderAlias, ...reportAliases]);
          const employees: OrgChartEmployee[] = Array.from(employeeMap.values());
          plan = buildSupplementalPlan(employees, leaderAlias);
        }
      }

      const total = 1 + FIXED_SUBFOLDERS.length + plan.personCount;
      setProgress({ current: 0, total });

      const bump = (by: number): void => {
        if (isMounted.current) setProgress((prev) => ({ current: prev.current + by, total: prev.total }));
      };

      // The working token for this run, seeded from the live ref (which reflects any token present
      // before the run started). Each ensureFreshToken() refresh updates it in place, so a run that
      // starts expired refreshes ONCE and every later step reuses that full-lifetime token instead
      // of re-reading the lagging ref and refreshing again (the ref only catches up asynchronously
      // via the setState -> useEffect sync, so it would still read the old token here).
      let currentToken: string | null = accessTokenRef.current;

      // Returns a token that is valid for the next WorkDocs call, silently refreshing first if the
      // current one has lapsed (or is within a small skew window of doing so). Only when the refresh
      // itself fails (refresh token dead) do we throw WORKDOCS_TOKEN_EXPIRED — which sets
      // `tokenExpired` and surfaces the "Reconnect WorkDocs" button as the fallback.
      const ensureFreshToken = async (): Promise<string> => {
        const expiresAt = currentToken ? getTokenExpiry(currentToken) : null;
        // Refresh if there is no usable token, its expiry can't be read, or it is at/near expiry.
        // The skew buffer avoids handing a token to a call that would race the expiry boundary.
        if (!currentToken || expiresAt === null || expiresAt <= Date.now() + TOKEN_EXPIRY_SKEW_MS) {
          const next = await refresh();
          if (!next) throw new Error('WORKDOCS_TOKEN_EXPIRED');
          currentToken = next;
          return next;
        }
        return currentToken;
      };

      // Root folder (find-or-create by name — recreates a deleted root naturally).
      const rootName = buildRootFolderName(leaderAlias);
      const rootMap = await findOrCreateFoldersUnder(parentRootFolderId, [rootName], await ensureFreshToken());
      const provisionedRootId = rootMap.get(rootName);
      if (!provisionedRootId) throw new Error('Failed to create root folder');
      bump(1);

      // The 5 fixed folders under the root, in one list+create-missing pass.
      const fixedMap = await findOrCreateFoldersUnder(provisionedRootId, [...FIXED_SUBFOLDERS], await ensureFreshToken());
      const supplementalId = fixedMap.get(SUPPLEMENTAL_DATA_FOLDER);
      if (!supplementalId) throw new Error('Failed to create Supplemental Context folder');
      bump(FIXED_SUBFOLDERS.length);

      // The recursive org tree under Supplemental Context. Levels are strictly ordered
      // (a parent's folder id must exist before its children); siblings within a level
      // fan out with bounded concurrency.
      const folderIdByLogin = new Map<string, string>();
      for (const level of plan.levels) {
        // Ensure a fresh token once per level (silently refreshing if the current one lapsed), then
        // fan the level's siblings out under that single token — a level is short enough that the
        // token won't lapse again mid-fan-out.
        const levelToken = await ensureFreshToken();
        await runWithConcurrency(level, BATCH_SIZE, async (group) => {
          const parentId = group.parentLogin === null ? supplementalId : folderIdByLogin.get(group.parentLogin);
          if (!parentId) {
            logger.warn('Baseball Cards: missing parent folder id for group', { parentLogin: group.parentLogin });
            // Count the skipped children as processed so the progress bar (whose total
            // assumes every planned folder) still reaches 100% instead of stalling.
            bump(group.childLogins.length);
            return;
          }
          const created = await findOrCreateFoldersUnder(parentId, group.childLogins, levelToken);
          created.forEach((id, login) => folderIdByLogin.set(login, id));
          bump(group.childLogins.length);
        });
        await sleep(BATCH_DELAY_MS);
      }

      // Persist the provisioned root id (best-effort — folders exist regardless).
      const saved = await updateBaseballCardWorkDocsFolderId(leaderAlias, provisionedRootId);
      if (isMounted.current) {
        setRootFolderId(provisionedRootId);
        if (!saved) {
          setError('Folders created, but the reference could not be saved. Click Set Up again to retry.');
        } else if (!fellBackToDirect) {
          // Clean success (folders created AND reference persisted) — signal consumers, e.g. the
          // page auto-advances the wizard to Step 3. The `!saved` branch above deliberately does
          // NOT bump: it leaves an error and keeps the forward gate closed for a retry. The
          // over-cap fallback also does NOT bump: folders exist and the gate is open, but we hold
          // on Step 1 so the user reads the "fell back to direct reports" warning before continuing.
          setProvisionSuccessCount((count) => count + 1);
        }
      }

      logger.info('Baseball Cards: WorkDocs folder provisioning complete', { leaderAlias, viewMode, personCount: plan.personCount });
    } catch (err) {
      if (!isMounted.current) return;
      if (err instanceof Error && err.message === 'WORKDOCS_TOKEN_EXPIRED') {
        // Token-specific failure: flag it so the UI offers a one-click "Reconnect WorkDocs" action.
        // Provisioning is idempotent, so reconnecting and letting the auto-provision effect re-run
        // resumes cleanly — folders already created are kept.
        setTokenExpired(true);
        setError('WorkDocs session expired. Click Reconnect to continue — folders already created are kept.');
      } else {
        handleError('Baseball Cards: WorkDocs folder provisioning failed', err);
        setError('Some folders could not be created. Click Set Up again to resume — existing folders are preserved.');
      }
    } finally {
      opInFlight.current = false;
      supersededByProvision.current = false;
      if (isMounted.current) setIsProvisioning(false);
    }
  }, [leaderAlias, accessToken, parentRootFolderId, viewMode, refresh]);

  const canProvision = isAuthenticated && !!parentRootFolderId && !isProvisioning;
  const rootFolderUrl = rootFolderId ? buildWorkDocsFolderUrl(rootFolderId) : null;

  return {
    rootFolderId,
    rootFolderUrl,
    isProvisioning,
    isVerifying,
    progress,
    error,
    warning,
    canProvision,
    provisionSuccessCount,
    tokenExpired,
    provision: () => void provision()
  };
};
